import { reportError } from '@/utils/tracking';
import { z } from 'zod';

// Where the records live. One entry per mirrored storage key, holding who the
// browser's copy belongs to and when the row it came from was written.
export const DURABLE_FILTER_SYNC_RECORDS_STORAGE_KEY = 'durableFilterSyncRecords';

const recordSchema = z.object({
    owner: z.string(),
    syncedAt: z.string().optional(),
});

const recordsSchema = z.record(z.string(), recordSchema);

export type DurableFilterSyncRecord = z.infer<typeof recordSchema>;

// Timestamps are the row's own `updatedAt`, which the backend writes, so
// comparing one against another never depends on a browser's clock.
export const toRowTimestamp = (updatedAt: Date | string): number => new Date(updatedAt).getTime();

const readRecords = (storage: Storage): Partial<Record<string, DurableFilterSyncRecord>> => {
    try {
        const stored = storage.getItem(DURABLE_FILTER_SYNC_RECORDS_STORAGE_KEY);
        if (stored === null) {
            return {};
        }

        const parsed = recordsSchema.safeParse(JSON.parse(stored));
        return parsed.success ? parsed.data : {};
    } catch (error) {
        reportError(error);
        return {};
    }
};

export const readSyncRecord = (
    storage: Storage,
    storageKey: string,
): DurableFilterSyncRecord | null => readRecords(storage)[storageKey] ?? null;

export const writeSyncRecord = (
    storage: Storage,
    storageKey: string,
    record: DurableFilterSyncRecord,
): void => {
    try {
        const records = readRecords(storage);
        records[storageKey] = record;
        storage.setItem(DURABLE_FILTER_SYNC_RECORDS_STORAGE_KEY, JSON.stringify(records));
    } catch (error) {
        reportError(error);
    }
};

export const clearSyncRecord = (storage: Storage, storageKey: string): void => {
    try {
        const remaining = Object.fromEntries(
            Object.entries(readRecords(storage)).filter(([key]) => key !== storageKey),
        );
        storage.setItem(DURABLE_FILTER_SYNC_RECORDS_STORAGE_KEY, JSON.stringify(remaining));
    } catch (error) {
        reportError(error);
    }
};
